import { type FC, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import type { EnhancedConfigTypeId } from '#api/enhancements/enhancedTmsApiTypes'
import { useGetConfigurationTypeIds } from '#api/tmsApiHooks'
import { Icon } from '#components/Icon'
import { IconButton } from '#components/IconButton'
import { Select, type SelectEntry } from '#components/Select'
import { Spinner } from '#components/Spinner'
import FilterIcon from '#icons/filter-16.svg'
import FilterOutlineIcon from '#icons/filter-outline-16.svg'
import WarningIcon from '#icons/warning-triangle-outline-16.svg'
import { SelectionField } from './SelectionField'
import { TextInput } from './TextInput'

export const ConfigurationTypeInput: FC<{
  configurationTypeId?: EnhancedConfigTypeId
  onSelected: (configurationTypeId: EnhancedConfigTypeId | undefined) => void
  kind?: 'inline' | 'full'
  disabled?: boolean
}> = ({ configurationTypeId, onSelected, kind, disabled }) => {
  const { t } = useTranslation()
  const [filterConfigurationTypes, setFilterConfigurationTypes] = useState<boolean>(true)
  const configurationTypeIds = useGetConfigurationTypeIds()

  const input = useMemo(() => {
    if (configurationTypeIds.isLoading) {
      return (
        <section className="split-flex">
          <Spinner size="s" />
          <span>{t('setup.configuration-type.loading', 'Loading configuration types…')}</span>
        </section>
      )
    }
    if (!configurationTypeIds.isSuccess) {
      return (
        <section className="has-error">
          <Icon iconUrl={WarningIcon} />
          <span>{t('setup.configuration-type.error.failed-loading-configuration-types', 'Failed loading configuration types')}</span>
        </section>
      )
    }

    const foundConfiguration = configurationTypeIds.data?.listOfConfigTypes?.find((c) => c.configTypeID === configurationTypeId)
    const data: SelectEntry<EnhancedConfigTypeId, string>[] = [
      {
        id: 'no-selection' as unknown as EnhancedConfigTypeId,
        value: t('setup.configuration-type.no-selection', 'No Configuration Type'),
      },
      ...(configurationTypeIds.data?.listOfConfigTypes ?? [])
        .map(
          (v) =>
            ({
              id: v.configTypeID,
              value: `${v.configTypeName ?? t('setup.configuration-type.unknown')} (${v.configTypeID})`,
            }) satisfies SelectEntry<EnhancedConfigTypeId, string>,
        )
        .toSorted((a, b) => a.value.localeCompare(b.value)),
    ]

    return (
      <Select
        label={kind === 'inline' ? undefined : t('setup.configuration-type.label', 'Configuration Type')}
        slim
        options={data}
        value={configurationTypeId}
        disabled={disabled}
        placeholder={foundConfiguration?.configTypeName ?? t('setup.configuration-type.unknown', 'Unknown Configuration Type')}
        onChange={(item) => {
          onSelected(item)
        }}
      />
    )
  }, [t, configurationTypeId, onSelected, configurationTypeIds, kind, disabled])

  return (
    <SelectionField>
      <IconButton
        className="icon"
        iconUrl={filterConfigurationTypes ? FilterIcon : FilterOutlineIcon}
        title={
          filterConfigurationTypes
            ? t('setup.configuration-type.filter.matching', 'Show matching configurations')
            : t('setup.configuration-type.filter.all', 'Show all configurations')
        }
        disabled={disabled}
        onClick={() => {
          setFilterConfigurationTypes(!filterConfigurationTypes)
        }}
      />
      {input}
      <TextInput
        type="number"
        value={configurationTypeId ?? ''}
        disabled={disabled}
        onChange={(x) => {
          if (!Number.isNaN(x.target.valueAsNumber)) {
            onSelected(x.target.value.length === 0 ? undefined : (x.target.valueAsNumber as EnhancedConfigTypeId))
          }
        }}
      />
    </SelectionField>
  )
}
